import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, Phone } from 'lucide-react';
import brand from '../../config/brand';

const countries = [
  { code: 'US', name: 'United States', dialCode: '+1' },
  { code: 'CA', name: 'Canada', dialCode: '+1' },
  { code: 'GB', name: 'United Kingdom', dialCode: '+44' },
  { code: 'AU', name: 'Australia', dialCode: '+61' },
  { code: 'DE', name: 'Germany', dialCode: '+49' },
  { code: 'FR', name: 'France', dialCode: '+33' },
  { code: 'ES', name: 'Spain', dialCode: '+34' },
  { code: 'IT', name: 'Italy', dialCode: '+39' },
  { code: 'NL', name: 'Netherlands', dialCode: '+31' },
  { code: 'SE', name: 'Sweden', dialCode: '+46' },
  { code: 'NO', name: 'Norway', dialCode: '+47' },
  { code: 'DK', name: 'Denmark', dialCode: '+45' },
  { code: 'FI', name: 'Finland', dialCode: '+358' },
  { code: 'IE', name: 'Ireland', dialCode: '+353' },
  { code: 'NZ', name: 'New Zealand', dialCode: '+64' },
  { code: 'SG', name: 'Singapore', dialCode: '+65' },
  { code: 'JP', name: 'Japan', dialCode: '+81' },
  { code: 'KR', name: 'South Korea', dialCode: '+82' },
  { code: 'IN', name: 'India', dialCode: '+91' },
  { code: 'BR', name: 'Brazil', dialCode: '+55' },
  { code: 'MX', name: 'Mexico', dialCode: '+52' },
  { code: 'CN', name: 'China', dialCode: '+86' },
  { code: 'HK', name: 'Hong Kong', dialCode: '+852' },
  { code: 'AE', name: 'United Arab Emirates', dialCode: '+971' },
  { code: 'SA', name: 'Saudi Arabia', dialCode: '+966' },
  { code: 'ZA', name: 'South Africa', dialCode: '+27' },
  { code: 'CH', name: 'Switzerland', dialCode: '+41' },
  { code: 'AT', name: 'Austria', dialCode: '+43' },
  { code: 'BE', name: 'Belgium', dialCode: '+32' },
  { code: 'PT', name: 'Portugal', dialCode: '+351' },
  { code: 'PL', name: 'Poland', dialCode: '+48' },
  { code: 'RU', name: 'Russia', dialCode: '+7' },
  { code: 'IL', name: 'Israel', dialCode: '+972' },
  { code: 'PH', name: 'Philippines', dialCode: '+63' },
  { code: 'TH', name: 'Thailand', dialCode: '+66' },
  { code: 'MY', name: 'Malaysia', dialCode: '+60' },
  { code: 'ID', name: 'Indonesia', dialCode: '+62' },
  { code: 'VN', name: 'Vietnam', dialCode: '+84' },
  { code: 'AR', name: 'Argentina', dialCode: '+54' },
  { code: 'CL', name: 'Chile', dialCode: '+56' },
  { code: 'CO', name: 'Colombia', dialCode: '+57' },
  { code: 'NG', name: 'Nigeria', dialCode: '+234' },
  { code: 'EG', name: 'Egypt', dialCode: '+20' },
  { code: 'PK', name: 'Pakistan', dialCode: '+92' },
  { code: 'BD', name: 'Bangladesh', dialCode: '+880' },
  { code: 'TR', name: 'Turkey', dialCode: '+90' },
  { code: 'GR', name: 'Greece', dialCode: '+30' },
  { code: 'CZ', name: 'Czech Republic', dialCode: '+420' },
  { code: 'RO', name: 'Romania', dialCode: '+40' },
  { code: 'HU', name: 'Hungary', dialCode: '+36' },
];

export default function AuthModal({ open, onClose, initialMode = 'login' }) {
  const [mode, setMode] = useState(initialMode);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, register } = useAuth();

  useEffect(() => {
    if (open) {
      setMode(initialMode);
    }
  }, [open, initialMode]);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    country: '',
    phone: '',
    tcpa_optin: false,
    marketing_optin: false,
    terms_accepted: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const selectedCountry = countries.find(c => c.code === formData.country);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (mode === 'login') {
        await login(formData.email, formData.password);
        toast.success('Welcome back!');
      } else {
        if (!formData.name) {
          toast.error('Please enter your name');
          setLoading(false);
          return;
        }
        if (!formData.country) {
          toast.error('Please select your country');
          setLoading(false);
          return;
        }
        if (!formData.terms_accepted) {
          toast.error('Please accept the Terms of Service and Privacy Policy');
          setLoading(false);
          return;
        }
        await register({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          country: formData.country,
          phone: formData.phone ? `${selectedCountry?.dialCode || ''}${formData.phone}` : '',
          tcpa_optin: formData.tcpa_optin,
        });
        toast.success('Account created successfully!');
      }
      onClose();
      setFormData({ 
        email: '', 
        password: '', 
        name: '', 
        country: '',
        phone: '',
        tcpa_optin: false, 
        marketing_optin: false, 
        terms_accepted: false 
      });
    } catch (error) {
      const message = error.response?.data?.detail || 'An error occurred';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-white border border-gray-200 shadow-2xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="auth-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl text-gray-900" style={{ fontFamily: "'Playfair Display', serif" }}>
            {mode === 'login' ? 'Welcome Back' : 'Create Free Account'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {mode === 'register' && (
            <>
              <div>
                <Label htmlFor="name" className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
                  Full Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-white border-gray-300 focus:border-blue-600 text-gray-900"
                  placeholder="John Doe"
                  data-testid="register-name-input"
                  required={mode === 'register'}
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
                  Country <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.country}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, country: value }))}
                >
                  <SelectTrigger 
                    className="bg-white border-gray-300 focus:border-blue-600 text-gray-900" 
                    data-testid="register-country-select"
                  >
                    <SelectValue placeholder="Select your country" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200 shadow-lg max-h-[300px] z-[100]">
                    {countries.map((country) => (
                      <SelectItem 
                        key={country.code} 
                        value={country.code} 
                        className="text-gray-700 hover:bg-gray-50 cursor-pointer"
                      >
                        {country.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
                  Phone Number
                </Label>
                <div className="flex gap-2">
                  <div className="w-24 flex-shrink-0">
                    <div className="bg-gray-50 border border-gray-300 rounded-md h-10 flex items-center justify-center text-gray-500 text-sm">
                      {selectedCountry?.dialCode || '+1'}
                    </div>
                  </div>
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" strokeWidth={1.5} />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10 bg-white border-gray-300 focus:border-blue-600 text-gray-900"
                      placeholder="555 123 4567"
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
              Email Address <span className="text-red-500">*</span>
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              className="bg-white border-gray-300 focus:border-blue-600 text-gray-900"
              placeholder="you@example.com"
              data-testid="auth-email-input"
              required
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
              Password <span className="text-red-500">*</span>
            </Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={handleChange}
                className="bg-white border-gray-300 focus:border-blue-600 text-gray-900 pr-10"
                placeholder="Enter your password"
                data-testid="auth-password-input"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {mode === 'register' && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              {/* Terms & Privacy Agreement */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="terms_accepted"
                  checked={formData.terms_accepted}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, terms_accepted: checked }))}
                  className="mt-1 border-gray-300 data-[state=checked]:bg-black data-[state=checked]:border-black"
                  data-testid="terms-checkbox"
                />
                <Label htmlFor="terms_accepted" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                  I agree to the{' '}
                  <Link to="/terms" className="text-blue-600 hover:underline" onClick={onClose}>
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:underline" onClick={onClose}>
                    Privacy Policy
                  </Link>{' '}
                  <span className="text-red-500">*</span>
                </Label>
              </div>

              {/* Transactional Messages Consent */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="tcpa_optin"
                  checked={formData.tcpa_optin}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, tcpa_optin: checked }))}
                  className="mt-1 border-gray-300 data-[state=checked]:bg-black data-[state=checked]:border-black"
                  data-testid="tcpa-checkbox"
                />
                <Label htmlFor="tcpa_optin" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                  By checking this box, I consent to receive transactional messages from {brand.name} related to my account, such as account creation confirmations and account status notifications.
                </Label>
              </div>

              {/* Marketing Messages Consent */}
              <div className="flex items-start space-x-3">
                <Checkbox
                  id="marketing_optin"
                  checked={formData.marketing_optin}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, marketing_optin: checked }))}
                  className="mt-1 border-gray-300 data-[state=checked]:bg-black data-[state=checked]:border-black"
                  data-testid="marketing-checkbox"
                />
                <Label htmlFor="marketing_optin" className="text-xs text-gray-500 leading-relaxed cursor-pointer">
                  By checking this box, I agree to receive marketing messages from {brand.name}, including promotional offers and updates.
                </Label>
              </div>

              {/* SMS Disclosure */}
              <div className="bg-gray-50 border border-gray-200  p-3 mt-4">
                <p className="text-[10px] text-gray-500 leading-relaxed">
                  <span className="text-gray-600">*</span>Consent to receive SMS messages is optional and not a condition for using {brand.name} services. Message and data rates may apply. You may receive up to 4 messages per week. You can opt out at any time by texting STOP. For assistance, text HELP or contact us at{' '}
                  <a href={`mailto:${brand.supportEmail}`} className="text-blue-600 hover:underline">
                    {brand.supportEmail}
                  </a>
                  . Carriers are not responsible for delayed or undelivered messages. All information collected during the SMS opt-in process will not be sold, rented, or shared. View our{' '}
                  <Link to="/terms" className="text-blue-600 hover:underline" onClick={onClose}>
                    Terms of Use
                  </Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:underline" onClick={onClose}>
                    Privacy Policy
                  </Link>.
                </p>
              </div>
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-black text-white hover:bg-gray-800 font-semibold uppercase tracking-wide py-2  mt-6"
            data-testid="auth-submit-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : mode === 'login' ? (
              'Sign In'
            ) : (
              'Create Free Account'
            )}
          </Button>
        </form>

        <div className="mt-4 text-center">
          <button
            onClick={() => setMode(mode === 'login' ? 'register' : 'login')}
            className="text-sm text-gray-500 hover:text-gray-900 transition-colors"
            data-testid="auth-mode-toggle"
          >
            {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
            <span className="text-blue-600">{mode === 'login' ? 'Sign Up' : 'Sign In'}</span>
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
